import { useContext } from 'react'
import { useSearchParams } from 'react-router-dom'

import { Loading, ToastContext } from '@reveni-io/reveni-ui'

import AppContext from '@/common/context/app.context'
import ChargebackErrorModal from '@/returns/components/ChargebackErrorModal/ChargebackErrorModal'
import useChargebackErrorModal from '@/returns/hooks/useChargebackErrorModal/useChargebackErrorModal'

import DeliveryMethodsCard from './DeliveryMethodsCard/DeliveryMethodsCard'
import Header from './Header/Header'
import useDeliveryMethods from './hooks/useDeliveryMethod/useDeliveryMethod'
import useOutOfPolicyModal from './hooks/useOutOfPolicyModal/useOutOfPolicyModal'
import useRefundMethod from './hooks/useRefundMethod/useRefundMethod'
import useReturn from './hooks/useReturn/useReturn'
import useShippingAddress from './hooks/useShippingAddress/useShippingAddress'
import useTransactionRMA from './hooks/useTransactionRMA/useTransactionRMA'
import OutOfPolicyModal from './OutOfPolicyModal/OutOfPolicyModal'
import ProductsCard from './ProductsCard/ProductsCard'
import SummaryCard from './SummaryCard/SummaryCard'
import * as Styles from './styles'

const TransactionRMAPageContent = () => {
  const [searchParams] = useSearchParams()
  const { info } = useContext(AppContext)
  const { setToastInfo } = useContext(ToastContext)
  const { fetchingOrder, rmaInfo } = useTransactionRMA({
    orderName: searchParams.get('orderName'),
    customerEmail: searchParams.get('customerEmail'),
    returnPortalId: info?.selectedReturnPortal?.id,
  })
  const { showChargebackModal, closeChargebackModal, handleChargebackError } = useChargebackErrorModal()
  const {
    createRma,
    updateRma,
    loading: loadingReturn,
  } = useReturn({ setToastInfo, returnPortalId: info?.selectedReturnPortal?.id, handleChargebackError })
  const { regionId } = useShippingAddress()
  const {
    gettingDeliveryMethods,
    getDeliveryMethods,
    deliveryMethods,
    deliveryMethod,
    onClickDeliveryMethod,
    onSelectTimeRange,
    onSelectPickupDate,
    onSaveDeliveryMethod,
    specialConstraints,
    changeNumberOfReturnLabels,
    numberOfReturnLabels,
    maxNumberOfReturnLabels,
    addingDeliveryMethod,
  } = useDeliveryMethods({
    setToastInfo,
    orderId: rmaInfo?.orderInfo?.id,
    regionId,
  })
  const { addingRefundMethod, onChangeRefundMethod } = useRefundMethod({ setToastInfo })
  const { showOutOfPolicyModal, closeOutOfPolicyModal } = useOutOfPolicyModal({
    orderOutOfPolicy: rmaInfo?.orderInfo?.outOfPolicy,
  })

  return (
    <Styles.Container>
      <Loading show={fetchingOrder}>
        <Header orderName={rmaInfo?.orderInfo?.name} orderOutOfPolicy={rmaInfo?.orderInfo?.outOfPolicy} />
        <Styles.MainContainer>
          <Styles.ColumnContainer>
            <ProductsCard
              lineItems={rmaInfo?.orderInfo?.line_items}
              merchantInfo={rmaInfo?.merchantInfo}
              createRma={createRma}
              updateRma={updateRma}
              getDeliveryMethods={getDeliveryMethods}
            />
            <DeliveryMethodsCard
              currency={rmaInfo?.orderInfo?.currency}
              loading={loadingReturn || gettingDeliveryMethods}
              getDeliveryMethods={getDeliveryMethods}
              deliveryMethods={deliveryMethods}
              deliveryMethod={deliveryMethod}
              onClickDeliveryMethod={onClickDeliveryMethod}
              onSelectTimeRange={onSelectTimeRange}
              onSelectPickupDate={onSelectPickupDate}
              onSaveDeliveryMethod={onSaveDeliveryMethod}
              specialConstraints={specialConstraints}
              changeNumberOfReturnLabels={changeNumberOfReturnLabels}
              numberOfReturnLabels={numberOfReturnLabels}
              maxNumberOfReturnLabels={maxNumberOfReturnLabels}
              addingDeliveryMethod={addingDeliveryMethod}
            />
          </Styles.ColumnContainer>
          <Styles.ColumnContainer>
            <SummaryCard
              loadingBreakdown={loadingReturn || addingDeliveryMethod || addingRefundMethod}
              loadingDeliveryMethodDetails={addingDeliveryMethod}
              onChangeRefundMethod={onChangeRefundMethod}
            />
          </Styles.ColumnContainer>
        </Styles.MainContainer>
      </Loading>
      <OutOfPolicyModal show={showOutOfPolicyModal} onClose={closeOutOfPolicyModal} />
      <ChargebackErrorModal show={showChargebackModal} onClose={closeChargebackModal} />
    </Styles.Container>
  )
}

export default TransactionRMAPageContent
